<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import Inbox from "./inbox.vue";
import emailContentEditor from "../_v2EmailContentEditor.vue";
import Schedule from "./schedule.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
import DateRange from "./dateRange.vue";
const notificationStore = useNotificationStore()

const props = defineProps({
    automationId: {
        type: Number,
        required: true,
    }
})

const currentStep = ref(null)
const newsletter = ref(null)
const editingName = ref(false)
const contentValidationIssues = ref([])
const state = ref('')
const showSendPreviewEmailModal = ref(false)
const previewEmailAddress = ref('')
const copying = ref(false)

const recipientCounts = reactive({
    total: 0,
    email: 0,
    phone: 0,
})

const loadAutomationData = async () => {
    if (props.automationId === null) {
        return
    }
    const res = await axios.get(route('api.bo.crm.automations.show', {automation_id: props.automationId}))

    newsletter.value = res.data.automation;
    newsletter.value.email_template.body = JSON.stringify([]);
}

const save = async () => {
    if (newsletter.value === null) {
        return
    }
    state.value = 'saving'

    await axios.put(route('api.bo.crm.newsletters.update', newsletter.value.id), newsletter.value)
    state.value = 'success'

    if (
        newsletter.value.schedule_type === 'send_immediately' ||
        newsletter.value.schedule_type === 'schedule'
    ) {
        window.location.reload()
    }
}

const onContentUpdate = (newContent) => {
    newsletter.value.email_template.body = newContent
}

const onContentValidation = (validationIssues) => {
    contentValidationIssues.value = validationIssues
}

const onIncludedTagsSelected = (tags) => {
    newsletter.value.included_lead_tags = tags
}

const onExcludedTagsSelected = (tags) => {
    newsletter.value.excluded_lead_tags = tags
}

const loadRecipientCount = async () => {
    if (! newsletter.value?.included_lead_tag_ids) {
        return;
    }

    const res = await axios.get(route('crm.leadtags.count'), {
        params: {
            included_tags: newsletter.value.included_lead_tag_ids,
            excluded_tags: newsletter.value.excluded_lead_tag_ids,
            once_assigned_in_time_range_start: newsletter.value.once_assigned_in_time_range_start,
            once_assigned_in_time_range_end: newsletter.value.once_assigned_in_time_range_end,
            delivery_types: ['email'],
        }
    });

    recipientCounts.total = res.data.leads_count
    recipientCounts.email = res.data.leads_with_email_count
    recipientCounts.phone = res.data.leads_with_phone_count
}

const sendPreviewEmail = async (email) => {
    if (email === 'myself') {
        email = window.myflow.user.email
    }

    const res = await axios.post(route('crm.communications.automations.dispatchTestMessages', newsletter.value.id), {
        email: email
    })

    if (res.data.success) {
        notificationStore.addNotification({
            type: 'success',
            message: _mft('communication:composer.previewEmailSent'),
            dismissAfter: 2500,
        })
    }
}

const updateNewsletterName = async () => {
    if (newsletter.value.name.length === 0) {
        return;
    }

    const res = await axios.put(route('api.bo.crm.newsletters.updateName', newsletter.value.id), {
        name: newsletter.value.name
    })

    if (res.data.success) {
        editingName.value = false
    }
}

const copyAutomation = async () => {
    if (copying.value === true) {
        return;
    }
    copying.value = true
    const res = await axios.post(route('crm.communications.automations.clone', newsletter.value.id))

    if (res.data.success) {
        window.location.href = route('bo.crm.newsletters.edit', res.data.automation_id)
    }
}

const inboxValidates = computed(() => {
    return newsletter.value?.email_template?.subject?.length > 0
})

const contentValidates = computed(() => {
    return newsletter.value?.email_template.body.length > 0 && contentValidationIssues.value.length === 0
})

const recipientsValidates = computed(() => {
    if (newsletter.value.send_rule === 'once_assigned_in_time_range') {
        return newsletter.value?.included_lead_tag_ids?.length > 0 &&
            newsletter.value?.once_assigned_in_time_range_start &&
            newsletter.value?.once_assigned_in_time_range_start
    }

    return newsletter.value?.included_lead_tag_ids?.length > 0
})

const canBeScheduled = computed(() => {
    return inboxValidates.value && recipientsValidates.value && contentValidates.value
})

const scheduleIsSet = computed(() => {
    return newsletter.value.schedule_type === 'send_immediately' ||
        (newsletter.value.schedule_type === 'schedule' && newsletter.value.activate_at)
})

const saveButtonLabel = computed(() => {
    if (newsletter.value.schedule_type === 'send_immediately') {
        return _mft('communication:automation.saveAndSend')
    }
    if (newsletter.value.schedule_type === 'schedule') {
        return _mft('communication:automation.saveAndSchedule')
    }

    return _mft('communication:automation.save')
})

watch(() => newsletter.value?.included_lead_tags, (tags) => {
    newsletter.value.included_lead_tag_ids = tags.map(tag => tag.id)
    loadRecipientCount()
}, {deep: true})

watch(() => newsletter.value?.excluded_lead_tags, (tags) => {
    newsletter.value.excluded_lead_tag_ids = tags.map(tag => tag.id)
    loadRecipientCount()
}, {deep: true})

onMounted(() => {
    loadAutomationData()
})

</script>
<template>
    <div>
        <div v-if="newsletter">
            <div class="md:sticky top-2 z-topbar">
                <frosted-bar>
                    <div class="flex items-center justify-between space-x-4 f">
                        <div class="flex space-x-1 items-start">
                            <h1>
                                {{ newsletter ? newsletter.name : '' }}
                            </h1>
                            <button-transparent
                                size="xs"
                                @click="editingName = true"
                            >
                                <i class="fa-regular fa-pen text-xs text-gray-400"></i>
                            </button-transparent>
                        </div>

                        <div class="flex items-center space-x-4">
                            <div class="flex flex-col">
                                <button-default
                                    @click="copyAutomation"
                                    :disabled="copying"
                                    class="justify-center"
                                >{{ _mft('communication:copyToNewCommunication') }}</button-default>
                            </div>


                            <button-more-options
                                :label="_mft('communication:composer.preview')"
                                alignment="right"
                                :cancel-on-outside-click="false"
                            >
                                <button-more-options-option
                                    @click=""
                                >
                                    <a
                                        :href="route('crm.communications.automations.preview', newsletter.id)"
                                        target="_blank"
                                        class="whitespace-nowrap"
                                    >
                                        {{ _mft('communication:previewInBrowser') }}
                                    </a>
                                </button-more-options-option>
                                <button-more-options-option>
                                    <span
                                        @click="sendPreviewEmail('myself')"
                                        class="whitespace-nowrap"
                                    >
                                        {{ _mft('communication:sendPreviewToSelf') }}
                                    </span>
                                </button-more-options-option>
                                <button-more-options-option
                                    @click="showSendPreviewEmailModal = true"
                                >
                                    <span class="whitespace-nowrap">
                                        {{ _mft('communication:sendPreviewToSomeoneElse') }}
                                    </span>
                                </button-more-options-option>
                            </button-more-options>

                            <button-success
                                @click="save"
                                :state="state"
                            >
                                {{
                                    saveButtonLabel
                                }}
                            </button-success>
                        </div>
                    </div>

                    <template #navigation>
                        <a
                            :href="route('bo.crm.newsletters.list')"
                            class="mf-link text-xs"
                        >
                            &larr; {{ _mft('communication:newsletter.domainNamePlural') }}
                        </a>
                    </template>
                </frosted-bar>
            </div>


            <div class="mt-8">
                <ui-stepped
                    :track-state="true"
                    :display-progress-bar="true"
                    @changestatusstep="currentStep = $event"
                >
                    <ui-stepped-step
                        title="Inbox"
                        value="inbox"
                        :completed="inboxValidates"
                        :unlocked="true"
                        :has-warning="!inboxValidates"
                        :active="true"
                    >
                        <div class="grid lg:grid-cols-2 gap-8 mt-12">
                            <div>
                                <h2>{{ _mft('communication:composer.inbox.content') }}</h2>
                                <inbox
                                    :newsletter="newsletter"
                                />
                            </div>
                            <div>
                                <div>
                                    <h2>{{ _mft('communication:composer.inbox.preview') }}</h2>
                                    <communications-inbox-preview
                                        :newsletter="newsletter"
                                    ></communications-inbox-preview>
                                </div>
                            </div>
                        </div>
                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:composer.steps.content')"
                        value="content"
                        :unlocked="true"
                        :has-warning="!contentValidates"
                        :completed="contentValidates"
                    >
                        <emailContentEditor
                            v-if="newsletter?.email_template"
                            class="mt-12"
                            ref="emailContentEditorRef"
                            :content="newsletter.email_template.block_editor_content.content"
                            :automationId="newsletter.id"
                            @update:emailContent="onContentUpdate"
                            @update:validation="onContentValidation"
                        />
                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:nRecipientsSelected', {count: recipientCounts.email})"
                        value="recipients"
                        :completed="recipientsValidates"
                        :unlocked="true"
                    >
                        <p
                            class="text-gray-500 text-center opacity-opacity mt-8"
                            :class="recipientCounts.email === 0 ? 'opacity-0' : 'opacity-100'"
                        >
                            {{ _mft('communication:nRecipientsSelected', {count: recipientCounts.email}) }}
                        </p>

                        <div
                            class="grid md:grid-cols-2 gap-8 mt-8"
                        >
                            <div>
                                <h2>{{ _mft('communication:tags.toInclude') }}</h2>
                                <p class="text-xs text-gray-500">
                                    {{ _mft('communication:newsletter.recipients.contactsWithTagsReceives') }}
                                </p>
                                <tags-multi-select-v2
                                    :allow-creation="false"
                                    :placeholder="_mft('crm:searchTags')"
                                    :pre-selected-tags="newsletter?.included_lead_tags.map(tag => tag.id) || []"
                                    :display-as="'list'"
                                    :display-counts="['email']"
                                    @tagsSelected="onIncludedTagsSelected"
                                />
                            </div>
                            <div>
                                <h2>{{ _mft('communication:tags.toExclude') }}</h2>
                                <p class="text-xs text-gray-500">
                                    {{ _mft('communication:newsletter.recipients.contactsWithTagsNotReceives') }}
                                </p>
                                <tags-multi-select-v2
                                    :allow-creation="false"
                                    :placeholder="_mft('crm:searchTags')"
                                    :pre-selected-tags="newsletter?.excluded_lead_tags.map(tag => tag.id) || []"
                                    :display-as="'list'"
                                    :display-counts="['email']"
                                    @tagsSelected="onExcludedTagsSelected"
                                />
                            </div>
                        </div>

                        <div
                            v-if="newsletter.send_rule === 'once_assigned_in_time_range'"
                            class="mt-8"
                        >
                            <h2>{{ _mft('communication:tags.assignedBetweenDates') }}</h2>
                            <date-range
                                :newsletter="newsletter"
                            ></date-range>
                        </div>

                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:composer.summary.automation.schedule')"
                        value="schedule"
                        :unlocked="canBeScheduled"
                        :completed="scheduleIsSet"
                        :tooltip="! canBeScheduled ? _mft('communication:completeStepsToSchedule') : null"
                    >
                        <schedule
                            :newsletter="newsletter"
                        />
                    </ui-stepped-step>

                    <template #action-right-global>
                        <button-success
                            v-if="currentStep === 'schedule'"
                            @click="save"
                            :state="state"
                        >
                            {{
                                saveButtonLabel
                            }}
                        </button-success>
                    </template>

                </ui-stepped>
            </div>

            <modal-basic
                :open="showSendPreviewEmailModal"
            >
                <template #title>
                    {{ _mft('communication:sendPreviewEmail') }}
                </template>
                <template #body>
                    <div>
                        <div class="space-y-4">
                            <input-text
                                :label="_mft('communication:sendPreviewEmail.recipientEmail')"
                                v-model:content="previewEmailAddress"
                            ></input-text>
                        </div>
                        <div class="flex items-center justify-end space-x-4 mt-4">
                            <button-base
                                @click="showSendPreviewEmailModal = false"
                            >
                                {{ _mft('shared:cancel') }}
                            </button-base>
                            <button-primary
                                :disabled="! previewEmailAddress || previewEmailAddress.length === 0"
                                @click="sendPreviewEmail(previewEmailAddress); showSendPreviewEmailModal = false"
                            >
                                {{ _mft('communication:sendPreview') }}
                            </button-primary>
                        </div>
                    </div>
                </template>
            </modal-basic>

            <modal-basic
                :open="editingName"
            >
                <template #title>
                    {{ _mft('communication:changeName') }}
                </template>
                <template #body>
                    <div>
                        <div class="space-y-4">
                            <input-text
                                :label="_mft('communication:automationName')"
                                maxlength="250"
                                v-model:content="newsletter.name"
                                autofocus
                            ></input-text>
                        </div>
                        <div class="flex items-center justify-end space-x-4 mt-4">
                            <button-base
                                @click="editingName = false"
                            >
                                {{ _mft('shared:cancel') }}
                            </button-base>
                            <button-primary
                                :disabled="newsletter.name.length === 0"
                                @click="updateNewsletterName"
                            >
                                {{ _mft('shared:action.update') }}
                            </button-primary>
                        </div>
                    </div>
                </template>
            </modal-basic>
        </div>
    </div>
</template>
